// MARK://dhk8/pos/lib/training-mode
// Training mode = the SAME PosLayout the cashier uses every day, but with a
// loud visual marker and (eventually) a mocked-data provider so a trainee can
// click through Floor → Order → Pay without touching the real tenant ledger.
//
// This replaces the old standalone `DemoFlow.tsx` route surface: there is no
// separate "demo" app any more — training is a flag on the one shell.
//
// Activation (any of):
//   1. `?mode=training` (or `?training=1`) in the URL when the app boots.
//   2. `localStorage["dhk8_pos_training"] === "1"` (sticks across reloads).
//   3. `setTrainingMode(true)` called at runtime (e.g. from a settings toggle).
//
// Other clusters that want mocked data in training mode should read
// `useTrainingMode()` and branch their data source on it. (Cluster A only owns
// the flag + the badge; the mocked catalog/orders provider is a follow-up — see
// open questions.)

import { createContext, createElement, useContext, useEffect, useState, type ReactNode } from 'react'

const LS_KEY = 'dhk8_pos_training'

/** Read the initial training-mode value from URL params + localStorage. */
function readInitial(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const params = new URLSearchParams(window.location.search)
    const m = params.get('mode')
    if (m === 'training' || params.get('training') === '1') return true
    return window.localStorage.getItem(LS_KEY) === '1'
  } catch {
    return false
  }
}

function persist(on: boolean): void {
  if (typeof window === 'undefined') return
  try {
    if (on) window.localStorage.setItem(LS_KEY, '1')
    else window.localStorage.removeItem(LS_KEY)
  } catch {
    /* ignore quota / disabled storage */
  }
}

interface TrainingModeContextValue {
  /** True when the shell is in training mode. */
  training: boolean
  /** Turn training mode on/off (persists to localStorage). */
  setTrainingMode: (on: boolean) => void
  /** Toggle helper. */
  toggleTrainingMode: () => void
}

const TrainingModeContext = createContext<TrainingModeContextValue | null>(null)

export function TrainingModeProvider({ children }: { children: ReactNode }) {
  const [training, setTraining] = useState<boolean>(() => readInitial())

  // Keep the value sticky once activated via URL: if the page was opened with
  // `?mode=training`, persist it so a reload (or in-app navigation that drops
  // the query string) keeps the trainee in training mode.
  useEffect(() => {
    if (training) persist(true)
  }, [training])

  // Reflect the flag on <html> so CSS (the topbar badge band, dimmed real-data
  // affordances, etc.) can react without prop-drilling.
  useEffect(() => {
    if (typeof document === 'undefined') return
    if (training) document.documentElement.setAttribute('data-training', 'on')
    else document.documentElement.removeAttribute('data-training')
  }, [training])

  function setTrainingMode(on: boolean) {
    persist(on)
    setTraining(on)
  }
  function toggleTrainingMode() {
    setTrainingMode(!training)
  }

  return createElement(
    TrainingModeContext.Provider,
    { value: { training, setTrainingMode, toggleTrainingMode } },
    children,
  )
}

/** Full training-mode context (flag + setters). Throws outside the provider. */
export function useTrainingModeContext(): TrainingModeContextValue {
  const ctx = useContext(TrainingModeContext)
  if (!ctx) throw new Error('useTrainingModeContext must be used inside <TrainingModeProvider>')
  return ctx
}

/** Convenience: just the boolean flag. Safe to call from any component below the provider. */
export function useTrainingMode(): boolean {
  return useTrainingModeContext().training
}

/**
 * Non-React read of the current flag (for stores / clients that can't use
 * hooks). Reads the live `<html data-training>` attribute the provider sets,
 * falling back to URL + localStorage before the provider mounts.
 */
export function isTrainingMode(): boolean {
  if (typeof document !== 'undefined' && document.documentElement.getAttribute('data-training') === 'on') {
    return true
  }
  return readInitial()
}
